import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const PayslipView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [payslip, setPayslip] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) {
            toast.error('Payslip identifier is missing.');
            navigate('/payslip');
            return;
        }
        fetchPayslip(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const getMonthName = (monthNumber) => {
        const months = [
            'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
            'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
        ];
        return months[monthNumber - 1] || '';
    };

    const formatCurrency = (value) => {
        if (value === '' || value === null || value === undefined) return '0.00';
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return '0.00';
        return numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch {
            return dateString;
        }
    };

    const fetchPayslip = async (payslipId) => {
        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            setLoading(false);
            return;
        }

        const currentUserId = localStorage.getItem('id');
        if (!currentUserId) {
            toast.error('User ID not found. Please login again.');
            setLoading(false);
            navigate('/payslip');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: 'get-payslip',
                currentUserId: currentUserId,
                id: payslipId
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;
            if (result?.success && result?.data) {
                setPayslip(result.data);
            } else {
                toast.error(result?.message || 'Failed to fetch payslip');
                navigate('/payslip');
            }
        } catch (error) {
            console.error('Error fetching payslip:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
            navigate('/payslip');
        } finally {
            setLoading(false);
        }
    };

    // --------- NEW: helper builders for rows (Earnings + Deductions) ----------

    const buildEarningRows = (p) => {
        const rows = [];
        // BASIC – always present row
        rows.push({ label: 'BASIC', amount: p.basic });

        if (p.da > 0) {
            rows.push({ label: 'DEARNESS ALLOWANCE', amount: p.da });
        }
        if (p.hra > 0) {
            rows.push({ label: 'HOUSE RENT ALLOWANCE', amount: p.hra });
        }
        if (p.specialAllowance > 0) {
            rows.push({ label: 'SPECIAL ALLOWANCE', amount: p.specialAllowance });
        }
        if (p.otherAllowance > 0) {
            rows.push({ label: 'OTHER ALLOWANCE', amount: p.otherAllowance });
        }
        if (p.miscAllowance > 0) {
            rows.push({ label: 'MISCELLANEOUS ALLOWANCE', amount: p.miscAllowance });
        }
        if (p.variablePay > 0) {
            rows.push({ label: 'VARIABLE PAY', amount: p.variablePay });
        }
        if (p.variablePayMonthly > 0) {
            rows.push({ label: 'VARIABLE PAY-MONTHLY', amount: p.variablePayMonthly });
        }
        if (p.arrearsAmount > 0) {
            rows.push({ label: 'ARREARS AMOUNT', amount: p.arrearsAmount });
        }
        if (p.statutoryBonus > 0) {
            rows.push({ label: 'STATUTORY BONUS', amount: p.statutoryBonus });
        }

        return rows;
    };

    // Deductions order: PF EE → PF ER → Gratuity → ESI → PT → TDS → Other → LOP
    const buildDeductionRows = (p) => {
        const rows = [];
        if (p.pfEe > 0 || p.pf > 0) {
            rows.push({ label: 'PROVIDENT FUND (EE)', amount: p.pfEe || p.pf });
        }
        if (p.pfEr > 0) {
            rows.push({ label: 'PROVIDENT FUND (ER)', amount: p.pfEr });
        }
        if (p.gratuity > 0) {
            rows.push({ label: 'GRATUITY', amount: p.gratuity });
        }
        if (p.esi > 0) {
            rows.push({ label: 'ESI', amount: p.esi });
        }
        if (p.pt > 0) {
            rows.push({ label: 'PROFESSIONAL TAX', amount: p.pt });
        }
        if (p.tdsDeduction > 0) {
            rows.push({ label: 'TDS DEDUCTION', amount: p.tdsDeduction });
        }
        if (p.otherDeduction > 0) {
            rows.push({ label: 'OTHER DEDUCTION', amount: p.otherDeduction });
        }
        if (p.lopDeduction > 0) {
            rows.push({ label: 'LOP DEDUCTION', amount: p.lopDeduction });
        }
        return rows;
    };

    const openPrintablePayslip = () => {
        if (!payslip) return;

        const formatDateForPrint = (dateString) => {
            if (!dateString) return '-';
            try {
                const date = new Date(dateString);
                const day = String(date.getDate()).padStart(2, '0');
                const month = date.toLocaleString('default', { month: 'short' });
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            } catch {
                return dateString;
            }
        };

        const earningRows = buildEarningRows(payslip);
        const deductionRows = buildDeductionRows(payslip);
        const fixedExtraRows = 3;
        const tableRowCount = Math.max(earningRows.length, deductionRows.length) + fixedExtraRows;

        const tableBodyRowsHtml = Array.from({ length: tableRowCount }, (_, index) => {
                const earning = earningRows[index];
                const deduction = deductionRows[index];
                return `
            <tr>
              <td style="padding:10px 12px; border:1px solid #000;">${earning ? earning.label : '&nbsp;'}</td>
              <td style="padding:10px 12px; border:1px solid #000; text-align:right;">${earning ? formatCurrency(earning.amount) : '&nbsp;'}</td>
              <td style="padding:10px 12px; border:1px solid #000; border-left:1px solid #000;">${deduction ? deduction.label : '&nbsp;'}</td>
              <td style="padding:10px 12px; border:1px solid #000; text-align:right;">${deduction ? formatCurrency(deduction.amount) : '&nbsp;'}</td>
            </tr>
          `;
            }).join('');

        const logoUrl = `${window.location.origin}${encodeURI('/assets/img/Website_logo_color logo-black text-1.png')}`;

        const payslipContent = `
      <style>
        @page {
          size: A4;
          margin: 15mm;
          background: white;
        }
        body {
          background: white;
          margin: 0;
          padding: 0;
        }
        @media print {
          #payslip-print-content {
            position: relative;
            left: auto;
            top: auto;
            width: 100%;
            margin: 0;
            padding: 0;
            background: white;
            max-width: 100%;
          }
          #payslip-print-content th {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: #e0f2f1 !important;
            color: #004d40 !important;
          }
          #payslip-print-content .payslip-title-bar {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: #009688 !important;
            color: #ffffff !important;
          }
          #payslip-print-content .net-row {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          #payslip-print-content .net-row td {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background-color: #f0f0f0 !important;
          }
        }
        #payslip-print-content {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 0;
          color: #333;
          font-size: 14px;
          width: 100%;
          max-width: 210mm;
          box-sizing: border-box;
          background: white;
        }
        #payslip-print-content .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: none;
        }
        #payslip-print-content .header-content {
          flex: 1;
        }
        #payslip-print-content .header img {
          max-height: 80px;
          margin-left: auto;
        }
        #payslip-print-content .title {
          margin: 8px 0 4px 0;
          font-size: 20px;
          font-weight: bold;
        }
        #payslip-print-content .subtitle {
          margin: 0;
          font-size: 13px;
          color: #666;
        }
        #payslip-print-content table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 12px;
        }
        #payslip-print-content td, #payslip-print-content th {
          border: 1px solid #000;
          padding: 8px 10px;
          font-size: 13px;
        }
        #payslip-print-content th {
          background-color: #e0f2f1;
          color: #004d40;
          font-weight: bold;
          text-align: left;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        #payslip-print-content .net-row td {
          background: #f0f0f0;
          font-size: 14px;
        }
        #payslip-print-content .footer {
          text-align: center;
          font-size: 12px;
          color: #666;
          font-style: italic;
          margin-top: 12px;
        }
      </style>
      <div id="payslip-print-content">
        <div class="header">
          <div class="header-content">
            <div class="title">InstaTenders Solutions Private Limited</div>
            <div class="subtitle"> 4/159, Labour Colony 1st Street, 3rd Phase, SIDCO Industrial Estate, Guindy, Chennai- 600032.</div>
          </div>
          <img src="${logoUrl}" alt="InstaTenders Logo" />
        </div>
        <div class="payslip-title-bar" style="text-align:center; margin-bottom: 18px; font-size: 16px; font-weight: bold; background-color: #009688; color: #ffffff; padding: 12px 20px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          Payslip For ${getMonthName(payslip.month)} ${payslip.year}
        </div>
        <table style="width:100%; border-collapse:collapse;">
          <colgroup>
            <col style="width:25%" />
            <col style="width:25%" />
            <col style="width:25%" />
            <col style="width:25%" />
          </colgroup>
          <tbody>
            <tr>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>Employee ID</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.employeeId || '-'}</td>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>Employee Name</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.employeeName || '-'}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>Designation</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.designationName || '-'}</td>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>Department</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.department || '-'}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>Bank</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.bankName || '-'}</td>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>Bank Account Number</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.bankAccountNumber || '-'}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>Date Of Joining</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${formatDateForPrint(payslip.dateOfJoining)}</td>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>PAN Number</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.panNumber || '-'}</td>
            </tr>
            <tr>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>PF Number</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.pfNumber || '-'}</td>
               <td style="padding:8px 12px; border:1px solid #000;"><strong>Standard Working Days</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.stdDays || 0}</td>
            </tr>
            <tr>
            <td style="padding:8px 12px; border:1px solid #000;"><strong>PF UAN Number</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.pfUanNumber || '-'}</td>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>Worked Days</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.workedDays || 0}</td>
            </tr>
            <tr>
             <td style="padding:8px 12px; border:1px solid #000;"><strong>Location</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.location || 'Chennai'}</td>
              <td style="padding:8px 12px; border:1px solid #000;"><strong>LOP Days</strong></td>
              <td style="padding:8px 12px; border:1px solid #000;">${payslip.lopDays || 0}</td>
            </tr>
          </tbody>
        </table>
        <table style="width:100%; border-collapse:collapse; margin-bottom:18px;">
          <colgroup>
            <col style="width:25%" />
            <col style="width:25%" />
            <col style="width:25%" />
            <col style="width:25%" />
          </colgroup>
          <thead>
            <tr>
              <th style="padding:12px; border:1px solid #000; background-color:#e0f2f1; color:#004d40; font-weight:bold; text-align:left;">Earnings</th>
              <th style="padding:12px; border:1px solid #000; background-color:#e0f2f1; color:#004d40; font-weight:bold; text-align:right; white-space:nowrap;">Amount in Rs.</th>
              <th style="padding:12px; border:1px solid #000; background-color:#e0f2f1; color:#004d40; font-weight:bold; text-align:left;">Deductions</th>
              <th style="padding:12px; border:1px solid #000; background-color:#e0f2f1; color:#004d40; font-weight:bold; text-align:right; white-space:nowrap;">Amount in Rs.</th>
            </tr>
          </thead>
          <tbody>
            ${tableBodyRowsHtml}
          </tbody>
          <tfoot>
            <tr>
              <td style="padding:12px; border:1px solid #000; font-weight:bold;">GROSS EARNINGS</td>
              <td style="padding:12px; border:1px solid #000; text-align:right; font-weight:bold;">${formatCurrency(payslip.grossEarnings)}</td>
              <td style="padding:12px; border:1px solid #000; font-weight:bold;">GROSS DEDUCTIONS</td>
              <td style="padding:12px; border:1px solid #000; text-align:right; font-weight:bold;">${formatCurrency((payslip.grossDeductions || 0))}</td>
            </tr>
            <tr class="net-row">
              <td style="padding:12px; border:1px solid #000; font-weight:bold; background:#f0f0f0;">NET PAY</td>
              <td style="padding:12px; border:1px solid #000; text-align:right; font-weight:bold; background:#f0f0f0;">${formatCurrency(payslip.netPay)}</td>
              <td style="padding:12px; border:1px solid #000; background:#f0f0f0;"></td>
              <td style="padding:12px; border:1px solid #000; background:#f0f0f0;"></td>
            </tr>
          </tfoot>
        </table>
        <div class="footer"><strong>This is a computer generated payslip and does not require signature and stamp.</strong></div>
      </div>
    `;

        const formatUsername = (name) => {
            if (!name) return 'Employee';
            return name
                .split(/\s+/)
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        };

        const monthName = getMonthName(payslip.month);
        const username = formatUsername(payslip.employeeName);
        const filename = `Payslip-${username} ${monthName} ${payslip.year}`;

        const originalTitle = document.title;
        document.title = filename;

        const safeTitle = String(filename).replace(/</g, '');

        const printHtml = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/><title>${safeTitle}</title></head><body>${payslipContent}</body></html>`;

        const iframe = document.createElement('iframe');
        iframe.setAttribute('title', 'Payslip print');
        iframe.setAttribute('aria-hidden', 'true');
        Object.assign(iframe.style, {
            position: 'fixed',
            left: '-9999px',
            top: '0',
            width: '210mm',
            minHeight: '297mm',
            border: '0',
            opacity: '0',
            pointerEvents: 'none',
            zIndex: '-1'
        });
        document.body.appendChild(iframe);

        const idoc = iframe.contentDocument || iframe.contentWindow.document;
        idoc.open();
        idoc.write(printHtml);
        idoc.close();

        const printWin = iframe.contentWindow;

        const cleanup = () => {
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
            }
            document.title = originalTitle;
        };

        let didPrint = false;
        const runPrint = () => {
            if (didPrint) return;
            didPrint = true;
            try {
                printWin.focus();
                printWin.print();
            } catch (err) {
                console.error('Print failed:', err);
                toast.error('Unable to open print or save as PDF. Please try again.');
            }
            setTimeout(cleanup, 2000);
        };

        // Android / mobile print preview needs layout + decode time before capture
        setTimeout(() => {
            if (printWin.document.readyState === 'complete') {
                runPrint();
            } else {
                printWin.addEventListener('load', runPrint, { once: true });
            }
            // Fallback if load does not fire (some WebViews)
            setTimeout(runPrint, 1200);
        }, 250);
    };

    // Precompute rows for screen view
    const earningRows = payslip ? buildEarningRows(payslip) : [];
    const deductionRows = payslip ? buildDeductionRows(payslip) : [];
    const fixedExtraRows = 3;
    const tableRowCount = Math.max(earningRows.length, deductionRows.length) + fixedExtraRows;

    return (
        <div className="employee-form payslip-view-root">
            <style>
                {`
                .payslip-view-root .payslip-inner {
                    max-width: 100%;
                    box-sizing: border-box;
                }
                .payslip-view-root .payslip-company-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 16px;
                    margin-bottom: 25px;
                    border-bottom: 2px solid #000;
                    padding-bottom: 15px;
                }
                .payslip-view-root .payslip-company-text {
                    flex: 1;
                    min-width: 0;
                }
                .payslip-view-root .payslip-company-logo {
                    max-height: 80px;
                    margin-left: auto;
                    flex-shrink: 0;
                    object-fit: contain;
                }
                .payslip-view-root .payslip-table-wrap {
                    width: 100%;
                    max-width: 100%;
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                    margin-bottom: 15px;
                }
                .payslip-view-root .payslip-data-table {
                    width: 100%;
                    max-width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                }
                .payslip-view-root .payslip-data-table th,
                .payslip-view-root .payslip-data-table td {
                    word-break: break-word;
                    overflow-wrap: anywhere;
                }
                @media (max-width: 767.98px) {
                    .payslip-view-root .payslip-company-header {
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                        gap: 12px;
                    }
                    .payslip-view-root .payslip-company-logo {
                        margin-left: 0 !important;
                        order: -1;
                        max-height: 56px;
                        max-width: min(240px, 100%);
                    }
                    .payslip-view-root .payslip-company-text h2 {
                        font-size: 16px !important;
                        line-height: 1.3 !important;
                    }
                    .payslip-view-root .payslip-company-text p {
                        font-size: 12px !important;
                        line-height: 1.45 !important;
                    }
                    .payslip-view-root .payslip-actions {
                        text-align: center !important;
                    }
                    .payslip-view-root .payslip-actions .btn {
                        width: 100%;
                    }
                    .payslip-view-root .payslip-table-wrap {
                        margin-left: -8px;
                        margin-right: -8px;
                        padding-left: 8px;
                        padding-right: 8px;
                        width: calc(100% + 16px);
                        max-width: 100vw;
                        box-sizing: border-box;
                    }
                    .payslip-view-root .payslip-data-table {
                        font-size: 11px;
                    }
                    .payslip-view-root .payslip-data-table th,
                    .payslip-view-root .payslip-data-table td {
                        padding: 6px 5px !important;
                    }
                    .payslip-view-root .payslip-th-amount {
                        white-space: normal !important;
                    }
                    .payslip-view-root .payslip-month-banner {
                        padding: 10px 12px !important;
                        margin-bottom: 16px !important;
                    }
                    .payslip-view-root .payslip-month-banner h3 {
                        font-size: 15px !important;
                        line-height: 1.35 !important;
                    }
                    .payslip-view-root .employee-content-card .card-body {
                        overflow-x: auto;
                        max-width: 100%;
                    }
                }
                `}
            </style>
            <section className="content">
                <div className="container-fluid">
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-8">
                            <h1 className="employee-title">
                                Payslip Details
                                <i className="fas fa-file-invoice-dollar ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                Detailed payslip view
                            </p>
                        </div>
                        <div className="col-md-4 employee-view-header-actions">
                            <button
                                type="button"
                                className="employee-back-btn"
                                onClick={() => {
                                    if (window.history.length > 1) {
                                        window.history.back();
                                    } else {
                                        navigate('/payslip');
                                    }
                                }}
                            >
                                <i className="fas fa-arrow-left mr-2"></i>
                                Back
                            </button>
                        </div>
                    </div>

                    <div className="card employee-content-card">
                        <div className="card-body">
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <Loading size={100} />
                                </div>
                            ) : !payslip ? (
                                <div className="employee-empty">Payslip is not available.</div>
                            ) : (
                                <>
                                    <div className="payslip-actions" style={{ textAlign: 'right', marginBottom: '15px' }}>
                                        <button type="button" className="btn btn-primary btn-sm" onClick={openPrintablePayslip}>
                                            <i className="fas fa-download mr-1"></i> Download / Print
                                        </button>
                                    </div>

                                    <div className="payslip-inner" style={{ padding: '10px 0 0 0' }}>
                                        <div className="payslip-company-header">
                                            <div className="payslip-company-text">
                                                <h2 style={{ margin: '6px 0 4px 0', fontSize: '22px', fontWeight: 'bold', color: '#333' }}>
                                                    InstaTenders Solutions Private Limited
                                                </h2>
                                                <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                                                    4/159, Labour Colony 1st Street, 3rd Phase, SIDCO Industrial Estate, Guindy, Chennai- 600032.
                                                </p>
                                            </div>
                                            <img
                                                className="payslip-company-logo"
                                                src="/assets/img/Website_logo_color logo-black text-1.png"
                                                alt="InstaTenders Logo"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>

                                        <div
                                            className="payslip-month-banner"
                                            style={{
                                            textAlign: 'center',
                                            marginBottom: '20px',
                                            backgroundColor: '#009688',
                                            padding: '12px 20px',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffffff', margin: 0 }}>
                                                Payslip For {getMonthName(payslip.month)} {payslip.year}
                                            </h3>
                                        </div>

                                        <div className="payslip-table-wrap" style={{ marginBottom: '25px' }}>
                                            <table className="payslip-data-table" style={{ marginBottom: '15px' }}>
                                                <colgroup>
                                                    <col style={{ width: '25%' }} />
                                                    <col style={{ width: '25%' }} />
                                                    <col style={{ width: '25%' }} />
                                                    <col style={{ width: '25%' }} />
                                                </colgroup>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Employee ID</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.employeeId || '-'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Employee Name</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.employeeName || '-'}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Designation</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.designationName || '-'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Department</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.department || '-'}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Bank</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.bankName || '-'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Bank Account Number</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.bankAccountNumber || '-'}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Date Of Joining</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {formatDate(payslip.dateOfJoining)}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>PAN Number</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.panNumber || '-'}
                                                        </td>

                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>PF Number</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.pfNumber || '-'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Standard Working Days</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.stdDays || 0}
                                                        </td>



                                                    </tr>
                                                    <tr>
                                                        {/* <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>ESI Number</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.esiNumber || '-'}
                                                        </td> */}
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>PF UAN Number</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.pfUanNumber || '-'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Worked Days</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.workedDays || 0}
                                                        </td>

                                                    </tr>
                                                    <tr>

                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>Location</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.location || 'Chennai'}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>LOP Days</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.lopDays || 0}
                                                        </td>


                                                    </tr>
                                                    {/* <tr>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            <strong>LOP Days</strong>
                                                        </td>
                                                        <td style={{ padding: '8px 12px', border: '1px solid #000' }}>
                                                            {payslip.lopDays || 0}
                                                        </td>
                                                    </tr> */}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="payslip-table-wrap" style={{ marginBottom: '25px' }}>
                                            <table className="payslip-data-table" style={{ marginBottom: '18px' }}>
                                                <colgroup>
                                                    <col style={{ width: '25%' }} />
                                                    <col style={{ width: '25%' }} />
                                                    <col style={{ width: '25%' }} />
                                                    <col style={{ width: '25%' }} />
                                                </colgroup>
                                                <thead>
                                                    <tr>
                                                        <th
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                backgroundColor: '#e0f2f1',
                                                                color: '#004d40',
                                                                fontWeight: 'bold',
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            Earnings
                                                        </th>
                                                        <th
                                                            className="payslip-th-amount"
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                backgroundColor: '#e0f2f1',
                                                                color: '#004d40',
                                                                fontWeight: 'bold',
                                                                textAlign: 'right',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            Amount in Rs.
                                                        </th>
                                                        <th
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                borderLeft: '1px solid #000',
                                                                backgroundColor: '#e0f2f1',
                                                                color: '#004d40',
                                                                fontWeight: 'bold',
                                                                textAlign: 'left',
                                                            }}
                                                        >
                                                            Deductions
                                                        </th>
                                                        <th
                                                            className="payslip-th-amount"
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                backgroundColor: '#e0f2f1',
                                                                color: '#004d40',
                                                                fontWeight: 'bold',
                                                                textAlign: 'right',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            Amount in Rs.
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {Array.from({ length: tableRowCount }, (_, index) => {
                                                        const earning = earningRows[index];
                                                        const deduction = deductionRows[index];
                                                        return (
                                                            <tr key={`payslip-row-${index}`}>
                                                                <td style={{ padding: '10px 12px', border: '1px solid #000' }}>
                                                                    {earning ? earning.label : '\u00A0'}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding: '10px 12px',
                                                                        border: '1px solid #000',
                                                                        textAlign: 'right',
                                                                    }}
                                                                >
                                                                    {earning ? formatCurrency(earning.amount) : '\u00A0'}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding: '10px 12px',
                                                                        border: '1px solid #000',
                                                                        borderLeft: '1px solid #000',
                                                                    }}
                                                                >
                                                                    {deduction ? deduction.label : '\u00A0'}
                                                                </td>
                                                                <td
                                                                    style={{
                                                                        padding: '10px 12px',
                                                                        border: '1px solid #000',
                                                                        textAlign: 'right',
                                                                    }}
                                                                >
                                                                    {deduction ? formatCurrency(deduction.amount) : '\u00A0'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>

                                                <tfoot>
                                                    <tr>
                                                        <td style={{ padding: '12px', border: '1px solid #000', fontWeight: 'bold' }}>
                                                            GROSS EARNINGS
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                textAlign: 'right',
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            {formatCurrency(payslip.grossEarnings)}
                                                        </td>
                                                        <td style={{ padding: '12px', border: '1px solid #000', fontWeight: 'bold' }}>
                                                            GROSS DEDUCTIONS
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                textAlign: 'right',
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            {formatCurrency((payslip.grossDeductions || 0))}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                fontWeight: 'bold',
                                                                backgroundColor: '#f0f0f0',
                                                            }}
                                                        >
                                                            NET PAY
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                textAlign: 'right',
                                                                fontWeight: 'bold',
                                                                backgroundColor: '#f0f0f0',
                                                            }}
                                                        >
                                                            {formatCurrency(payslip.netPay)}
                                                        </td>
                                                        <td
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                backgroundColor: '#f0f0f0',
                                                            }}
                                                        />
                                                        <td
                                                            style={{
                                                                padding: '12px',
                                                                border: '1px solid #000',
                                                                backgroundColor: '#f0f0f0',
                                                            }}
                                                        />
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>

                                        <div
                                            style={{
                                                textAlign: 'center',
                                                marginTop: '15px',
                                                paddingTop: '15px',
                                                borderTop: '1px solid #000',
                                            }}
                                        >
                                            <p
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#666',
                                                    margin: 0,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                <strong>
                                                    This is a computer generated payslip and does not require signature and stamp.
                                                </strong>
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PayslipView;
